import { Archive, Check, ChevronDown, Copy, Download, Ellipsis, Inbox, KeyRound, Pencil, Play, Plus, Radar, ShieldCheck, Trash2, UserRound, Users } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import toast from "react-hot-toast";
import AccordionCard from "../../../components/AccordionCard.tsx";
import AdvancedModelFold from "../../../components/agent/AdvancedModelFold.tsx";
import BackupCodesList from "../../../components/BackupCodesList.tsx";
import BulkActionBar from "../../../components/BulkActionBar.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import IdentityCell, { TenantCell, UserCell } from "../../../components/IdentityCell.tsx";
import ImageUploadField from "../../../components/ImageUploadField.tsx";
import KpiTile from "../../../components/KpiTile.tsx";
import LinkTile from "../../../components/LinkTile.tsx";
import LiveUpdatesPill from "../../../components/LiveUpdatesPill.tsx";
import LogoBadge from "../../../components/LogoBadge.tsx";
import DataProviderLogo from "../../../components/DataProviderLogo.tsx";
import DetailDrawer from "../../../components/DetailDrawer.tsx";
import ContactCard from "../../../components/contacts/ContactCard.tsx";
import NavTabs from "../../../components/NavTabs.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import Panel from "../../../components/Panel.tsx";
import OwnershipChip from "../../../components/OwnershipChip.tsx";
import RelativeTime from "../../../components/RelativeTime.tsx";
import ReviewToggle, { type ReviewState } from "../../../components/ReviewToggle.tsx";
import PursueReviewToggle from "../../../components/review/PursueReviewToggle.tsx";
import ReviewStatusChips from "../../../components/review/ReviewStatusChips.tsx";
import ShortcutHints from "../../../components/review/ShortcutHints.tsx";
import SnoozeMenu from "../../../components/review/SnoozeMenu.tsx";
import RowActions from "../../../components/RowActions.tsx";
import SecretCredentialForm from "../../../components/SecretCredentialForm.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import { FoldList, FoldRow } from "../../../components/FoldList.tsx";
import SettingRow from "../../../components/SettingRow.tsx";
import SlackLogo from "../../../components/SlackLogo.tsx";
import AgentActivity from "../../../components/AgentActivity.tsx";
import AgentRunStatusIcon from "../../../components/agent/AgentRunStatusIcon.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import StatusIcon, { StatusLabel } from "../../../components/StatusIcon.tsx";
import ThemeModeToggle from "../../../components/ThemeModeToggle.tsx";
import AuthCard from "../../../components/AuthCard.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import DropdownMenu from "../../../components/DropdownMenu.tsx";
import NoSelectionPrompt from "../../../components/NoSelectionPrompt.tsx";
import PaginationControls from "../../../components/PaginationControls.tsx";
import TotpInput from "../../../components/TotpInput.tsx";
import SavedViewsMenu from "../../../components/filters/SavedViewsMenu.tsx";
import UserAvatar from "../../../components/UserAvatar.tsx";
import ColumnPickerMenu, { type ColumnGroup } from "../../../components/ColumnPickerMenu.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import ScopeToggle from "../../../components/filters/ScopeToggle.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import OnboardingPanel from "../../../components/dashboard/org/OnboardingPanel.tsx";
import WorkingNowPanel from "../../../components/dashboard/org/WorkingNowPanel.tsx";
import {
  type OnboardingStepKey,
  type OrgDashboardTenant,
  type OrgLiveRun,
  type ProspectListContactEntry,
} from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";

const HOUR = 60 * 60 * 1000;
const ago = (ms: number) => new Date(Date.now() - ms).toISOString();

const STEP_KEYS: OnboardingStepKey[] = ["claude", "profile", "logo", "signal_workflow", "agent_workflow", "crm", "first_decision"];
const demoTenant = (id: string, name: string, done: number): OrgDashboardTenant => ({
  id,
  name,
  slug: id,
  logo_url: null,
  owned: true,
  inbox: 4,
  worklist: 2,
  done_in_period: 1,
  hits_in_period: 12,
  prospects_hit_in_period: 9,
  daily_hits_14d: [0, 1, 0, 2, 3, 1, 0, 0, 4, 2, 1, 0, 3, 2],
  active_workflows: 2,
  onboarding: STEP_KEYS.map((key, i) => ({ key, done: i < done })),
});
const DEMO_TENANTS = [demoTenant("acme", "Acme Norway", 4), demoTenant("nordic", "Nordic Heat", 6)];
const DEMO_MANY_TENANTS = [
  ...DEMO_TENANTS,
  demoTenant("fjord", "Fjord Logistics", 7),
  demoTenant("polar", "Polar Robotics", 2),
  demoTenant("tind", "Tind Software", 5),
  demoTenant("vik", "Vik Energy", 7),
];
const demoRun = (id: string, agent_type: string, subject: string, activity: Partial<OrgLiveRun>): OrgLiveRun => ({
  id,
  agent_type,
  agent_name: null,
  status: "running",
  tenant_id: "acme",
  subject,
  prospect_order_id: null,
  signal_id: null,
  started_at: ago(0.1 * HOUR),
  completed_at: null,
  created_at: ago(0.1 * HOUR),
  activity_kind: null,
  activity_tool: null,
  activity_detail: null,
  activity_at: null,
  ...activity,
});
const DEMO_LIVE = [
  demoRun("r1", "prospecting", "Nordic SaaS", { activity_kind: "tool_use", activity_tool: "WebSearch", activity_detail: "heat pump installers Bergen" }),
  demoRun("r2", "signal_research", "Bergen Elektro AS", { activity_kind: "tool_use", activity_tool: "WebFetch", activity_detail: "bergenelektro.no" }),
  demoRun("r3", "contact_research", "Fjord Bygg AS", { activity_kind: "assistant_text", activity_detail: "The CFO joined in March; the board lists two new members." }),
];

const ROWS = [
  { id: "1", name: "Ada Lovelace", email: "ada@example.com", status: "completed", seen: ago(2 * HOUR) },
  { id: "2", name: null, email: "no.name@example.com", status: "running", seen: ago(30 * 24 * HOUR) },
  { id: "3", name: "A person with a very long name that has to truncate", email: "long@example.com", status: "failed", seen: null },
];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const DEMO_CODES = ["7K2QH9XA", "M4PZ8R1C", "Q9WD3NTE", "B6YH2KLU", "X1CF7VJS", "R8NE5GQD"];

/**
 * Every shared building block in its states, for checking the design system
 * in one place (DESIGN.md). Developer-only.
 */
// One spec of every kind, on local state, for the filter bar demo.
const DEMO_FILTERS: FilterSpec[] = [
  {
    kind: "options",
    key: "country",
    label: "Country",
    param: "country",
    pinned: true,
    options: [
      { value: "NO", label: "🇳🇴 Norway" },
      { value: "SE", label: "🇸🇪 Sweden" },
      { value: "DK", label: "🇩🇰 Denmark" },
      { value: "FI", label: "🇫🇮 Finland" },
    ],
  },
  {
    kind: "options",
    key: "rating",
    label: "Match",
    param: "min_rating",
    multiple: false,
    default: ["3"],
    noneValue: "all",
    noneLabel: "Any",
    options: [
      { value: "5", label: "≥ 5" },
      { value: "4", label: "≥ 4" },
      { value: "3", label: "≥ 3" },
    ],
  },
  { kind: "boolean", key: "matched", label: "Matched", param: "matched", group: "Status", labels: { yes: "Matched", no: "Not matched" } },
  { kind: "range", key: "revenue", label: "Revenue", group: "Financials", minParam: "rev_min", maxParam: "rev_max", unit: "NOK" },
  { kind: "dateRange", key: "published", label: "Published", group: "Dates", fromParam: "from", toParam: "to" },
  { kind: "text", key: "buyer", label: "Buyer", group: "People", param: "buyer" },
  {
    kind: "custom",
    key: "since",
    label: "Since",
    params: ["since"],
    summary: (get) => get("since"),
  },
];

type PersonColumn = "seen" | "created";
const PERSON_COLUMN_GROUPS: ColumnGroup<PersonColumn>[] = [
  {
    key: "dates",
    label: "Dates",
    columns: [
      { key: "seen", label: "Last seen" },
      { key: "created", label: "Created" },
    ],
  },
];

function FoldDemo() {
  const [open, setOpen] = useState<string[]>(["financials"]);
  const toggle = (key: string) => setOpen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  return (
    <FoldList>
      <FoldRow label="Key facts" summary="Software · 42 employees" expanded={open.includes("facts")} onToggle={() => toggle("facts")}>
        <Typography variant="body2">Opened content mounts here.</Typography>
      </FoldRow>
      <FoldRow label="Financials" summary="Revenue NOK 45M · 2025" expanded={open.includes("financials")} onToggle={() => toggle("financials")}>
        <Typography variant="body2">A compact table, loaded on first open.</Typography>
      </FoldRow>
      <FoldRow label="Job ads" expanded={open.includes("jobs")} onToggle={() => toggle("jobs")}>
        <Typography variant="body2">Without a gist.</Typography>
      </FoldRow>
    </FoldList>
  );
}

const demoContact = (id: string, name: string, title: string, labels: Record<string, string>): ProspectListContactEntry => ({
  id,
  collection_id: null,
  master_contact_id: id,
  contact_name: name,
  title,
  headline: null,
  photo_url: null,
  reachability: { email: `${name.split(" ")[0].toLowerCase()}@example.com`, phone: null, linkedin_url: null },
  master_company_id: null,
  company_entry_id: null,
  company_name: "Example AS",
  added_by_user_id: null,
  added_by_agent_run_id: null,
  added_with_method: "manual",
  created_at: new Date().toISOString(),
  disqualified_at: null,
  disqualified_by_user_id: null,
  disqualified_by_agent_run_id: null,
  disqualification_reason: null,
  accepted_at: null,
  accepted_by_user_id: null,
  accepted_by_agent_run_id: null,
  inclusion_reason: null,
  note: null,
  labels,
  signal_trigger_count: 0,
  missing_required_properties: [],
});
const DEMO_CONTACTS = [
  demoContact("c1", "Ingrid Nilsen", "Chief Operating Officer", { Seniority: "C-level", Priority: "High" }),
  demoContact("c2", "Lars Berg", "Head of Sales", {}),
];

export default function UiKitPage() {
  const demoState = useLocalFilterState();
  const demoFilters = useFilters(DEMO_FILTERS, { state: demoState, search: { param: "q", placeholder: "Search companies" } });
  const [scope, setScope] = useState<"all" | "mine">("all");
  const [digest, setDigest] = useState(true);
  const [anyYesNo, setAnyYesNo] = useState("any");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [totp, setTotp] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const peopleState = useLocalFilterState();
  const peopleFilters = useFilters([], {
    state: peopleState,
    search: { param: "q", placeholder: "Search name or email" },
  });
  const [hiddenColumns, setHiddenColumns] = useState<PersonColumn[]>([]);
  const [labelColumns, setLabelColumns] = useState<string[]>([]);
  const toggleLabelColumn = (column: string) =>
    setLabelColumns((shown) =>
      shown.includes(column) ? shown.filter((c) => c !== column) : [...shown, column],
    );
  const search = peopleFilters.get("q") ?? "";
  const [review, setReview] = useState<ReviewState>("pending");
  const [model, setModel] = useState("");
  const [secret, setSecret] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openContact, setOpenContact] = useState<string | null>("c1");
  // What the held live list demo has waiting.
  const [livePending, setLivePending] = useState<"new" | "removed" | "none">("new");
  const rows = ROWS.filter((r) => `${r.name ?? ""} ${r.email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <PageHeader
        title="UI kit"
        description="The shared components every page is built from · see svc-frontend/DESIGN.md"
        actions={
          <>
            <Button variant="outlined">Secondary</Button>
            <Button variant="contained" startIcon={<Plus />} onClick={() => toast.success("Saved")}>
              Primary action
            </Button>
          </>
        }
      />

      <Stack spacing={3}>
        <TableCard
          toolbar={
            <FilterBar
              filters={demoFilters}
              count="1,204 companies"
              views={<SavedViewsMenu filters={demoFilters} context={{}} />}
              leading={
                <ScopeToggle
                  aria-label="Scope"
                  value={scope}
                  onChange={setScope}
                  options={[
                    { value: "all", label: "All" },
                    { value: "mine", label: "Mine", badge: 3 },
                  ]}
                />
              }
            />
          }
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Filter state: <code>{demoState.searchParams.toString() || "(defaults)"}</code>
            </Typography>
          </Box>
        </TableCard>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <KpiTile label="Matched" icon={<Radar />} value={1234} caption="12% of 10,283 evaluated" to="#">
              <LinearProgress variant="determinate" value={12} sx={{ height: 6 }} />
            </KpiTile>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <KpiTile label="Qualified" icon={<Users />} value={87} emphasis delta={5} deltaLabel="24h" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <KpiTile label="Needs you" icon={<Inbox />} color="warning" value="4.2 MB" dense caption="Dense, string value" />
          </Grid>
        </Grid>

        <TableCard
          toolbar={
            <FilterBar
              filters={peopleFilters}
              count={`${formatNumber(rows.length)} people`}
              actions={
                <ColumnPickerMenu
                  groups={PERSON_COLUMN_GROUPS}
                  hidden={hiddenColumns}
                  onChange={setHiddenColumns}
                  defaultHidden={[]}
                  optIn={{
                    label: "Label columns",
                    columns: ["Region", "Tier"],
                    shown: labelColumns,
                    onToggle: toggleLabelColumn,
                  }}
                />
              }
            />
          }
        >
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Status</TableCell>
                {!hiddenColumns.includes("seen") && <TableCell>Last seen</TableCell>}
                {!hiddenColumns.includes("created") && <TableCell>Created</TableCell>}
                {labelColumns.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <UserCell displayName={r.name} email={r.email} tag={i === 0 ? "You" : undefined} />
                  </TableCell>
                  <TableCell>
                    <StatusChip kind="job" value={r.status} />
                  </TableCell>
                  {!hiddenColumns.includes("seen") && (
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <RelativeTime value={r.seen} />
                    </TableCell>
                  )}
                  {!hiddenColumns.includes("created") && (
                    <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                      <FormattedDate value={ago(400 * 24 * HOUR)} />
                    </TableCell>
                  )}
                  {labelColumns.map((column) => (
                    <TableCell key={column}>-</TableCell>
                  ))}
                  <TableCell padding="checkbox" align="right">
                    <RowActions
                      label={r.name ?? r.email}
                      primary={{ label: "Run now", icon: <Play fontSize="small" />, onClick: () => toast("Running") }}
                      actions={[
                        { label: "Edit", icon: <Pencil fontSize="small" />, onClick: () => toast("Edit") },
                        {
                          label: "Delete",
                          icon: <Trash2 fontSize="small" />,
                          destructive: true,
                          onClick: () => wait(800).then(() => toast.success("Deleted")),
                          confirm: { title: "Delete person", body: <>Delete <strong>{r.email}</strong>?</> },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <EmptyTableRow colSpan={5 - hiddenColumns.length + labelColumns.length}>
                  No people match your search
                </EmptyTableRow>
              )}
            </TableBody>
          </ScrollTable>
        </TableCard>

        <Panel title="Panel with a table" subtitle="ScrollTable scrolls sideways when the columns don't fit" disablePadding>
          <ScrollTable>
            <TableHead>
              <TableRow>
                {["Queue", "Pending", "Running", "Failed (24h)", "Oldest pending", "Throughput", "Last run"].map((c) => (
                  <TableCell key={c} sx={{ whiteSpace: "nowrap" }}>
                    {c}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {["signal-engine", "crawl-queue"].map((q, i) => (
                <TableRow key={q} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{q}</TableCell>
                  {[1204 * (i + 1), 48, i, "3 min", "2,400 / h", "just now"].map((v, j) => (
                    <TableCell key={j} sx={{ whiteSpace: "nowrap" }}>
                      {v}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </Panel>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel fill title="Panel" subtitle="A titled section with an action" count={3} action={<Button size="small">Action</Button>}>
              <Stack spacing={1.5}>
                <TenantCell name="Acme Norway" caption="acme.no" tag="Primary" />
                <IdentityCell avatar={<LogoBadge size={32}><SlackLogo /></LogoBadge>} name="With a LogoBadge" caption="Any avatar fits" to="#" />
                <IdentityCell
                  avatar={<LogoBadge size={32}><DataProviderLogo name="GetLeads" size={32} /></LogoBadge>}
                  name="DataProviderLogo"
                  caption="A data provider's initials"
                />
              </Stack>
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel fill title="Statuses">
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <StatusIcon kind="ok" label="Ok" />
                  <StatusIcon kind="attention" label="Attention" />
                  <StatusIcon kind="error" label="Error" />
                  <StatusIcon kind="off" label="Off" />
                  <StatusIcon kind="loading" />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <StatusLabel kind="ok" label="Active" />
                  <StatusLabel kind="attention" label="Needs reconnect" />
                  <StatusLabel kind="error" label="Missing 2 scopes" />
                </Stack>
                <Stack direction="row" spacing={1}>
                  {["pending", "running", "completed", "succeeded", "partial", "failed", "cancelled", "unknown_value"].map(
                    (s) => (
                      <StatusChip key={s} kind="job" value={s} />
                    ),
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {["ran", "off", "failed", "stopped"].map((s) => (
                    <StatusChip key={s} kind="tick" value={s} />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  {["queued", "running", "completed", "partial", "failed", "cancelled"].map((s) => (
                    <AgentRunStatusIcon key={s} status={s} agentName="Contact research" error="Agent hit its turn limit" />
                  ))}
                </Stack>
                <Stack direction="row" spacing={3} sx={{ alignItems: "center" }}>
                  <IdentityCell
                    name="Kahoot"
                    tag={
                      <AgentActivity
                        runs={[{ id: "a", agent_type: "contact_research", agent_name: "Contact research", status: "running" }]}
                      />
                    }
                  />
                  <IdentityCell
                    name="Nordic SaaS scaleups"
                    tag={
                      <AgentActivity
                        runs={[
                          { id: "b", agent_type: "signal_research", agent_name: null, status: "running" },
                          { id: "c", agent_type: "custom", agent_name: "Tender scout", status: "queued" },
                        ]}
                      />
                    }
                  />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    <AgentActivity
                      runs={[{ id: "d", agent_type: "prospecting", agent_name: "Prospecting agent", status: "running" }]}
                      showLabel
                    />
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                  {["pending", "accepted", "awaiting_approval", "done", "reopened", "new_hits"].map((s) => (
                    <StatusChip key={s} kind="prospect_status" value={s} />
                  ))}
                  {["signal", "agent", "manual"].map((s) => (
                    <StatusChip key={s} kind="prospect_hit_kind" value={s} />
                  ))}
                  {["verified", "qualified", "inconclusive"].map((s) => (
                    <StatusChip key={s} kind="research_verdict" value={s} />
                  ))}
                  <StatusChip kind="contact_signal" value="open_to_work" />
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <ReviewToggle value={review} onChange={(next) => next && setReview(next)} />
                  <OwnershipChip link={{ is_primary: true, primary_organization: null, shared_with: [{ id: "o2", name: "Partner Org" }] }} />
                  <OwnershipChip link={{ is_primary: false, primary_organization: { id: "o2", name: "Partner Org" }, shared_with: [] }} />
                </Stack>
              </Stack>
            </Panel>
          </Grid>
        </Grid>

        <TableCard
          notice={
            <LiveUpdatesPill
              pendingNew={livePending === "new" ? 3 : 0}
              pendingRemoved={livePending === "none" ? 0 : 1}
              onApply={() => setLivePending("none")}
            />
          }
          toolbar={
            <Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
              <Button size="small" onClick={() => setLivePending("new")}>
                New and removed rows
              </Button>
              <Button size="small" onClick={() => setLivePending("removed")}>
                Removed rows only
              </Button>
            </Stack>
          }
        >
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {["Kahoot", "Nordic Heat", "Fjord Logistics"].map((name, i) => {
                // Gone from the server, held in place (dimmed) until Show.
                const removed = i === 1 && livePending !== "none";
                return (
                  <TableRow key={name} hover aria-disabled={removed || undefined} sx={removed ? { opacity: 0.5 } : undefined}>
                    <TableCell>{name}</TableCell>
                    <TableCell>
                      <StatusChip kind="prospect_status" value={removed ? "done" : "pending"} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ScrollTable>
        </TableCard>

        <BulkActionBar count={3} sticky={false} onClear={() => toast("Cleared")}>
          <Button size="small" variant="contained">Add to collection</Button>
          <Button size="small" variant="outlined" color="error">Disqualify</Button>
        </BulkActionBar>

        <Grid container spacing={1.5}>
          {["Q4 SaaS targets", "Hiring signals", "Fintech Sweden"].map((t, i) => (
            <Grid key={t} size={{ xs: 12, sm: 6, md: 3 }}>
              <LinkTile to="#" title={t} caption={`${(i + 1) * 120} prospects`} muted={i === 2} />
            </Grid>
          ))}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <LinkTile to="#" title="+3 more" more />
          </Grid>
        </Grid>

        <OnboardingPanel tenants={DEMO_TENANTS.slice(0, 1)} canManageOrg />
        <OnboardingPanel tenants={DEMO_TENANTS} canManageOrg={false} />
        <OnboardingPanel tenants={DEMO_MANY_TENANTS} canManageOrg />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <WorkingNowPanel live={DEMO_LIVE} tenants={new Map(DEMO_TENANTS.map((t) => [t.id, t]))} showTenant />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <WorkingNowPanel live={[]} tenants={new Map()} showTenant={false} />
          </Grid>
        </Grid>

        <Panel
          title="Advanced fold"
          subtitle="Rarely changed options (the Claude model, live catalog), folded; always the last item of a form"
        >
          <Stack spacing={2}>
            <TextField label="Title" size="small" defaultValue="Acme prospects" fullWidth />
            <AdvancedModelFold value={model} onChange={setModel} />
          </Stack>
        </Panel>

        <Panel title="Secret credential form">
          <SecretCredentialForm
            name="API key"
            stored
            hint="a1b2"
            updatedAt={ago(26 * HOUR)}
            updatedByEmail="ada@example.com"
            encryptionConfigured
            canManage
            value={secret}
            onChange={setSecret}
            fieldLabel="New API key"
            onSave={() => toast.success("Saved")}
            saveDisabled={!secret}
            onTest={() => toast("Testing")}
            onClear={() => wait(800)}
            clearConfirm={{ title: "Clear API key", body: "Purchases stop until a new key is set." }}
          />
        </Panel>

        <Panel title="Buttons and chips" subtitle="One contained primary per surface; outlined is a soft tint, never a border">
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Button variant="contained" startIcon={<Plus />}>Create list</Button>
              <Button variant="outlined" startIcon={<Download />}>Export</Button>
              <Button>Cancel</Button>
              <Button variant="contained" color="error">Delete</Button>
              <Button variant="outlined" color="error">Remove</Button>
              <Button variant="outlined" color="success">Pursue</Button>
              <Button variant="contained" loading>Save</Button>
              <Button variant="outlined" disabled>Disabled</Button>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <ButtonGroup variant="contained">
                <Button startIcon={<Check />}>Approve</Button>
                <Button aria-label="More approve options"><ChevronDown fontSize="small" /></Button>
              </ButtonGroup>
              <IconButton aria-label="Edit"><Pencil fontSize="small" /></IconButton>
              <IconButton aria-label="Delete"><Trash2 fontSize="small" /></IconButton>
              <TextField placeholder="A 32px field" sx={{ width: 160 }} />
              <Chip label="Draft" />
              <Chip variant="outlined" label="Global" />
              <Chip variant="outlined" color="primary" label="Acme AS" />
              <Chip variant="outlined" color="success" label="Live" />
              <Chip variant="outlined" color="warning" label="Unsaved changes" />
              <Chip variant="outlined" color="error" label="Failing" />
            </Stack>
          </Stack>
        </Panel>

        <Panel title="More controls" subtitle="Quiet buttons, menus, pickers, confirms, paging and loading">
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Button color="neutral">Clear all</Button>
              <DropdownMenu label="Actions" buttonProps={{ variant: "outlined" }} minWidth={200}>
                <ListSubheader>Copy</ListSubheader>
                <MenuItem>
                  <ListItemIcon>
                    <Copy />
                  </ListItemIcon>
                  <ListItemText>Duplicate</ListItemText>
                </MenuItem>
                <MenuItem selected>
                  <ListItemIcon>
                    <Pencil />
                  </ListItemIcon>
                  <ListItemText>Rename</ListItemText>
                </MenuItem>
                <MenuItem disabled>
                  <ListItemIcon>
                    <Archive />
                  </ListItemIcon>
                  <ListItemText>Archive</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ color: "error.main" }}>
                  <ListItemIcon sx={{ color: "inherit" }}>
                    <Trash2 />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </DropdownMenu>
              <DropdownMenu icon={<Ellipsis />} tooltip="More" aria-label="More actions">
                <MenuItem>Duplicate</MenuItem>
              </DropdownMenu>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={anyYesNo}
                onChange={(_, next: string | null) => next && setAnyYesNo(next)}
                aria-label="Has website"
              >
                <ToggleButton value="any">Any</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
                Delete…
              </Button>
              <ConfirmDialog
                open={confirmOpen}
                title="Delete list"
                confirmLabel="Delete"
                onConfirm={() => setConfirmOpen(false)}
                onClose={() => setConfirmOpen(false)}
              >
                The list and its entries are removed for everyone.
              </ConfirmDialog>
            </Stack>
            <PaginationControls
              rowsPerPage={rowsPerPage}
              pageItemCount={rowsPerPage}
              total={1204}
              cursorStackDepth={1}
              hasPrev
              hasNext
              onChangeRowsPerPage={setRowsPerPage}
              onPrev={() => {}}
              onNext={() => {}}
            />
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <CenteredSpinner py={1} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Skeleton width="80%" />
                <Skeleton width="60%" />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TotpInput value={totp} onChange={setTotp} />
              </Grid>
            </Grid>
          </Stack>
        </Panel>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel title="No selection prompt" subtitle="A page that needs a tenant and has none" fill>
              <NoSelectionPrompt kind="tenant" />
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel title="Auth card" subtitle="Signed-out pages: login, MFA, invitation" fill>
              {/* Full height on its own page; trimmed here to fit the panel. */}
              <Box sx={{ "& > .MuiBox-root": { minHeight: 0, py: 0 } }}>
                <AuthCard title="Sign in to Vayken" subtitle="Use your work email">
                  <Button variant="contained" fullWidth>
                    Continue
                  </Button>
                </AuthCard>
              </Box>
            </Panel>
          </Grid>
        </Grid>

        <div>
          <NavTabs
            label="Demo sections"
            tabs={[
              { label: "This page", to: "/settings/ui-kit", icon: <UserRound /> },
              { label: "Account security", to: "/account/security", icon: <KeyRound /> },
            ]}
          />
          <Panel title="Settings panel" subtitle="SettingRows in a Panel disablePadding" disablePadding>
            <SettingRow
              icon={<KeyRound />}
              label="Password"
              description="Last changed 3 months ago"
              action={<Button size="small" variant="outlined">Change password</Button>}
            />
            <SettingRow
              icon={<ShieldCheck />}
              label="Authenticator app"
              description="Signing in asks for a code from your app"
              action={<StatusLabel kind="ok" label="On" />}
            />
            <SettingRow label="Theme" description="A control on the right" action={<ThemeModeToggle />} />
            <SettingRow
              label="Weekly digest"
              description="A toggle: plain Switch, styled once in theme.ts"
              action={<Switch checked={digest} onChange={(e) => setDigest(e.target.checked)} />}
            />
          </Panel>
        </div>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel title="Image upload field" subtitle="Crops to a circle before it uploads" fill>
              <ImageUploadField
                preview={<UserAvatar displayName="Ada Lovelace" pictureUrl={photo} size={72} tooltip={false} />}
                hasImage={!!photo}
                accept={["image/png", "image/jpeg", "image/webp"]}
                crop={{ shape: "circle" }}
                caption="PNG, JPG or WebP"
                onUpload={async (file) => {
                  await wait(600);
                  setPhoto(URL.createObjectURL(file));
                }}
                onRemove={async () => setPhoto(null)}
                removeConfirm={{ title: "Remove photo", body: "Initials are shown instead." }}
              />
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel title="Backup codes list" fill>
              <BackupCodesList codes={DEMO_CODES} />
            </Panel>
          </Grid>
        </Grid>

        <Panel title="Review" subtitle="Deciding on a reviewed row: the prospects inbox and the signals share these">
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
              <PursueReviewToggle
                value={review}
                onChange={(next) => next && setReview(next)}
                pursueAndSnooze={{ onClick: () => toast("Pursue & snooze"), snoozed: false, title: "Pursue & snooze" }}
              />
              <ShortcutHints canDecide />
            </Stack>
            <ReviewStatusChips
              subject={{ id: "d", review_state: "accepted", awaiting_approval: true, snoozed_until: ago(-7 * 24 * HOUR), done_at: null }}
              reopenedAt={ago(HOUR)}
              newHits={3}
            />
            <ReviewStatusChips
              subject={{ id: "e", review_state: "disqualified", awaiting_approval: false, snoozed_until: ago(2 * HOUR), done_at: null }}
            />
            <ReviewStatusChips
              subject={{ id: "f", review_state: "accepted", awaiting_approval: false, snoozed_until: null, done_at: ago(HOUR) }}
            />
            <SnoozeMenu onSnooze={(until) => toast(until ? `Snoozed until ${until}` : "Snooze taken off")} />
          </Stack>
        </Panel>

        <Panel
          title="Detail drawer"
          subtitle="A row opened from a list; drag its left edge to resize, double-click to reset"
          action={<Button size="small" onClick={() => setDrawerOpen(true)}>Open drawer</Button>}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            The width is saved per user and shared by every detail drawer.
          </Typography>
        </Panel>
        <DetailDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          overline="Prospect · Norway · example.com"
          title="Example AS"
          actions={<Button size="small" variant="outlined">Action</Button>}
        >
          <Typography variant="body2">The body scrolls under the header.</Typography>
        </DetailDrawer>

        <Panel
          title="Contact card"
          subtitle="A person inside a drawer; the open one takes the people colour. It opens a ContactDrawer out of the drawer's left edge, the colour flooding from the card"
        >
          <Stack spacing={1} sx={{ maxWidth: 560 }}>
            {DEMO_CONTACTS.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                selected={openContact === c.id}
                onOpen={() => setOpenContact((open) => (open === c.id ? null : c.id))}
                controls={c.id === "c1" && <ReviewToggle value={review} onChange={(next) => next && setReview(next)} />}
              />
            ))}
          </Stack>
        </Panel>

        <Panel title="Panel without padding" subtitle="For a table or list of rows" disablePadding>
          <EmptyState icon={<Inbox />} title="Nothing here yet" description="An inline empty state sits inside an existing box." />
        </Panel>

        <div>
          <SectionHeading title="Section heading" count={2} action={<Button size="small">Action</Button>} />
          <Stack spacing={1.5}>
            <AccordionCard
              logo={<SlackLogo />}
              title="Accordion card"
              summary="Status at a glance, settings on demand"
              status={{ kind: "ok", label: "Connected" }}
            >
              <Typography variant="body2">Settings go here.</Typography>
            </AccordionCard>
            <AccordionCard title="Without a logo" summary="Needs a look" status={{ kind: "attention", label: "Needs reconnect" }}>
              <Typography variant="body2">Settings go here.</Typography>
            </AccordionCard>
            <AccordionCard
              logo={<Inbox sx={{ color: "text.secondary" }} />}
              title="With a count, edge to edge"
              count={2}
              summary="disablePadding: a table or rows under a divider"
              disablePadding
            >
              <SettingRow label="First row" description="Rows run edge to edge" action={<Button size="small">Edit</Button>} />
              <SettingRow label="Second row" description="Like Panel disablePadding" action={<Button size="small">Edit</Button>} />
            </AccordionCard>
          </Stack>
        </div>

        <Panel title="Fold list" subtitle="A micro accordion for side details: one line each, opened on click">
          <FoldDemo />
        </Panel>

        <EmptyState
          variant="panel"
          icon={<Inbox />}
          title="No collections yet"
          description="A panel empty state stands in for a page's content."
          action={<Button variant="contained" startIcon={<Plus />}>Create collection</Button>}
        />
      </Stack>
    </>
  );
}
